import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { ChevronDown, Check } from 'lucide-react';
import { useModal } from 'context/ModalContext';
import { useContent } from 'context/ContentContext';
import { ModalOption } from './ModalOption';

const SectionContainer = styled.div`
  margin-bottom: 1.6rem;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionButton = styled.button`
  width: 100%;
  background: ${({ theme }) => theme.colors.secondary};
  border: none;
  padding: 1.6rem 2rem;
  cursor: pointer;
  transition: all 0.2s;
  text-align: left;

  &:hover {
    background: ${({ theme }) => theme.colors.secondaryDark};
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 1.2rem;
  flex: 1;
`;

const StatusIndicator = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${({ $status, theme }) =>
    $status === 'empty' ? theme.colors.offWhiteDark : theme.colors.success};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s;

  svg {
    color: ${({ theme }) => theme.colors.white};
  }
`;

const StatusNumber = styled.span`
  font-size: 1.4rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.white};
`;

const SectionInfo = styled.div`
  flex: 1;
`;

const SectionTitle = styled.h3`
  font-size: 1.6rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.black};
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.8rem;
`;

const RequiredBadge = styled.span`
  font-size: 1.2rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.white};
  background: ${({ theme }) => theme.colors.error};
  padding: 0.2rem 0.8rem;
  border-radius: 6px;
  text-transform: uppercase;
`;

const SectionDescription = styled.p`
  font-size: 1.4rem;
  color: ${({ theme }) => theme.colors.primaryDark};
  opacity: 0.8;
  margin: 0.4rem 0 0;
  line-height: 1.4;
`;

const ExpandIcon = styled.div`
  color: ${({ theme }) => theme.colors.white};
  transition: transform 0.3s;
  transform: rotate(${({ $isOpen }) => ($isOpen ? '180deg' : '0')});
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CollapsibleContent = styled.div`
  max-height: ${({ $isOpen }) => ($isOpen ? '1000px' : '0')};
  overflow: hidden;
  transition: max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  background: ${({ theme }) => theme.colors.white};
`;

const ContentWrapper = styled.div`
  padding: 1.6rem;
  display: grid;
  gap: 1.2rem;
`;

export const ModalSection = ({ group, isOpen, onToggle }) => {
  const { selectedModifiers, handleModifierChange } = useModal();
  const { messages } = useContent();

  const getSelectionCount = () => {
    const selection = selectedModifiers[group.id];
    if (!selection) return 0;
    return Array.isArray(selection) ? selection.length : 1;
  };

  const isGroupComplete = () => {
    const count = getSelectionCount();
    const minRequired = group.minRequired || (group.required ? 1 : 0);
    return count >= minRequired;
  };

  const getDescription = () => {
    const selectedCount = getSelectionCount();

    if (isGroupComplete())
      return `${selectedCount} ${messages?.modal?.selected || 'selected'}`;

    if (group.minRequired && group.maxAllowed) {
      if (group.minRequired === group.maxAllowed) {
        return `${messages?.modal?.selectExactly || 'Select exactly'} ${group.minRequired}`;
      }
      return `${messages?.modal?.select || 'Select'} ${group.minRequired} ${messages?.modal?.to || 'to'} ${group.maxAllowed}`;
    }
    if (group.minRequired) {
      return `${messages?.modal?.selectAtLeast || 'Select at least'} ${group.minRequired}`;
    }
    if (group.maxAllowed) {
      return `${messages?.modal?.selectUpTo || 'Select up to'} ${group.maxAllowed}`;
    }
    return messages?.modal?.optionalSelection || 'Optional selection';
  };

  const selectedCount = getSelectionCount();
  const status = selectedCount > 0 ? 'hasSelection' : 'empty';

  const isRadioGroup = () => {
    return group.maxSelections === 1 || group.selectionType === 'single';
  };

  const isModifierSelected = (modifierId) => {
    const selection = selectedModifiers[group.id];
    if (Array.isArray(selection)) {
      return selection.includes(modifierId);
    }
    return selection === modifierId;
  };

  const handleModifierClick = (modifierId) => {
    if (isRadioGroup()) {
      handleModifierChange(group.id, modifierId);
    } else {
      const currentSelection = selectedModifiers[group.id] || [];
      const newSelection = isModifierSelected(modifierId)
        ? currentSelection.filter((id) => id !== modifierId)
        : [...currentSelection, modifierId];

      if (group.maxSelections && newSelection.length > group.maxSelections) {
        return;
      }

      handleModifierChange(group.id, newSelection);
    }
  };

  return (
    <SectionContainer>
      <SectionButton onClick={onToggle}>
        <SectionHeader>
          <HeaderLeft>
            <StatusIndicator $status={status}>
              {selectedCount > 0 ? (
                <Check size={18} strokeWidth={3} />
              ) : (
                <StatusNumber />
              )}
            </StatusIndicator>
            <SectionInfo>
              <SectionTitle>
                {group.name}
                {group.required && (
                  <RequiredBadge>
                    {messages?.modal?.required || 'Required'}
                  </RequiredBadge>
                )}
              </SectionTitle>
              <SectionDescription>{getDescription()}</SectionDescription>
            </SectionInfo>
          </HeaderLeft>
          <ExpandIcon $isOpen={isOpen}>
            <ChevronDown size={24} />
          </ExpandIcon>
        </SectionHeader>
      </SectionButton>

      <CollapsibleContent $isOpen={isOpen}>
        <ContentWrapper>
          {group.modifiers.map((modifier) => (
            <ModalOption
              key={modifier.id}
              modifier={modifier}
              isSelected={isModifierSelected(modifier.id)}
              isRadio={isRadioGroup()}
              onClick={() => handleModifierClick(modifier.id)}
              disabled={modifier.available === false}
            />
          ))}
        </ContentWrapper>
      </CollapsibleContent>
    </SectionContainer>
  );
};

ModalSection.propTypes = {
  group: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    required: PropTypes.bool,
    minRequired: PropTypes.number,
    maxAllowed: PropTypes.number,
    maxSelections: PropTypes.number,
    selectionType: PropTypes.string,
    modifiers: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        available: PropTypes.bool,
      }),
    ).isRequired,
  }).isRequired,
  isOpen: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
};

export default ModalSection;
